import { apiFetch } from '@/services/core/apiFetch'
import { getContext } from '@/services/networks/networksApi'
import type { Creative } from '@/services/creatives/creativesApi'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`

export type DataViewModel = 'LineItemView' | 'OrderView' | 'AdvertiserView' | 'CreativeView'

// Typed shape for the LineItemView dataView response
export interface LineItemDataView {
  lineItem: Record<string, unknown>
  advertiser: Record<string, unknown>
  creatives: Record<string, Creative & {
    layoutParameters: Record<string, { id: string; url: string } | string | null>
    constraintsToAssets: Record<string, { id: string; url: string }>
  }>
  notes: Record<string, unknown>
  tier?: Record<string, unknown>
  team?: Record<string, unknown>
}

export interface DataViewRequest {
  viewModel: DataViewModel
  parameters: Record<string, string>
}

export interface DataViewResult<T> {
  viewModel: DataViewModel
  dataView: T
}

export interface DataViewResponse<T> {
  results: DataViewResult<T>[]
}

export const DataViewApi = {
  fetch: async <T>(requests: DataViewRequest[], includeStats = false): Promise<DataViewResult<T>[]> => {
    const url = `${API_BASE}/dataview?includeStats=${includeStats}&context=${getContext()}`
    const res = await apiFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requests),
    })
    if (!res.ok) throw new Error(`DataView request failed (${res.status})`)
    const data: DataViewResponse<T> = await res.json()
    return data.results
  },

  fetchOne: async <T>(viewModel: DataViewModel, id: string, includeStats = false): Promise<T> => {
    const results = await DataViewApi.fetch<T>([{ viewModel, parameters: { id } }], includeStats)
    if (!results[0]) throw new Error(`No dataView result for ${viewModel} ${id}`)
    return results[0].dataView
  },
}
